import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';
import type Moment from 'moment';

import {Dialog} from 'shells/dialog/dialog';
import {getUserFacingNoun} from 'shells/edition/get-user-facing-noun';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {FooterBar} from 'shells/modal/footer-bar';
import {PrepareToSendBreadcrumbs} from 'shells/prepare-to-send/prepare-to-send-breadcrumbs';
import {PrepareToSendButtons} from 'shells/prepare-to-send/prepare-to-send-buttons';
import {PrepareToSendWrapper} from 'shells/prepare-to-send/prepare-to-send-wrapper';
import {roundTimeToNextQuarter} from 'shells/select-time-picker/helpers';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';

import type {CampaignType} from 'nutshell-graphql-types';
import type {
    SmsBroadcastFragment_edition as Edition,
    SendEdition as SendEditionAudienceMutation,
    SendEditionVariables as SendEditionAudienceMutationVariables,
} from 'nutshell-graphql-types';

import SEND_EMAIL_EDITION from '../../../editions/prepare-to-send-modal/graphql/mutations/send-email-marketing-edition.graphql';
import {
    getBusinessHoursStart,
    getError,
    getSelectedRecipientsFromAudience,
} from '../../../editions/prepare-to-send-page/helpers';
import {
    NewRecipientsSelectionView,
    RECIPIENT_TYPE_SAVED_LIST,
} from '../../../editions/prepare-to-send-page/new-recipients-selection-view';
import type {SelectedRecipients} from '../../../editions/prepare-to-send-page/new-recipients-selection-view';
import {NewScheduleView} from '../../../editions/prepare-to-send-page/new-schedule-view';
import {LIMITED_CAMPAIGN_USER_DEFAULT_RECIPIENTS} from '../../../editions/prepare-to-send-page/prepare-to-send-page';

import {useGetSessionUser} from '../../../../core/use-get-session-user';

import {MAX_BROADCAST_RECIPIENTS} from '../../consts';

import {useSendSmsEdition} from '../graphql/hooks';

import {CheckRequiredItems} from './check-required-items';
import {getIssueCount, getAudienceError} from './helpers';
import {ReviewAndSend} from './review-and-send';

type Props = {
    onClose: () => void;
    edition: Edition;
    isLoading: boolean;
    type: CampaignType;
    from: string | null | undefined;
    isScheduling: boolean;
    setIsScheduling: (arg1: boolean) => void;
    setIsRescheduling: (arg1: boolean) => void;
    routeToEngagement: () => void;
};

export function PrepareToSendPage(props: Props) {
    const [isShowingSendConfirmation, setIsShowingSendConfirmation] =
        React.useState<boolean>(false);
    const [sendButtonRef, setSendButtonRef] = React.useState<HTMLElement | null | undefined>(null);
    const [currentView, setCurrentView] = React.useState<string>('prepare');

    const {user} = useGetSessionUser();
    const canAccessFullCampaigns = Boolean(user && user.permissions.canAccessFullCampaigns);
    const defaultSelectedRecipients = canAccessFullCampaigns
        ? null
        : LIMITED_CAMPAIGN_USER_DEFAULT_RECIPIENTS;
    const [selectedRecipients, setSelectedRecipients] = React.useState<
        SelectedRecipients | null | undefined
    >(
        props.edition.audience
            ? getSelectedRecipientsFromAudience(props.edition.audience, true)
            : defaultSelectedRecipients
    );
    // @ts-expect-error
    const [scheduledDate, setScheduledDate] = React.useState<Moment>(
        roundTimeToNextQuarter(getBusinessHoursStart())
    );
    /*
    Because we're using the same breadcrumbs and schedule page as Em Broadcasts, we carry over this logic, which the copied comment below explains:

    "Our breadcrumbs on this page will update with the scheduled date, to say
    `sending on $DATE`. We don't want to show this scheduled time until the user
    has visited this page, however. I tried to add this functionality by making
    the scheduled time nullable, but many parts of the schedule page require a
    time value, making it very difficult to make that value null. Instead, I'm
    going to add this state and simply hide the value on the breadcrumbs until
    the schedule page is visited."
    */
    const [visitedSchedulePage, setVisitedSchedulePage] = React.useState<boolean>(false);

    const {sendEdition, isSending} = useSendSmsEdition();
    const [sendEditionAudience, {loading}] = useMutation<
        SendEditionAudienceMutation,
        SendEditionAudienceMutationVariables
    >(SEND_EMAIL_EDITION);

    const userFacingNoun = getUserFacingNoun(props.type);
    const recipientCount =
        selectedRecipients && selectedRecipients.recipientSelection
            ? selectedRecipients.recipientSelection.recipientCount
            : null;
    const sendToText =
        selectedRecipients && selectedRecipients.recipientSelection
            ? selectedRecipients.recipientSelection.name
            : '';
    const audienceError = getAudienceError(props.edition, selectedRecipients);
    const {errorMessage, shouldDisable, showUpgradeBanner} = getError(
        props.edition.gateError,
        audienceError,
        currentView
    );
    const isPrepareViewButtonDisabled = Boolean(
        getIssueCount(
            props.edition.content.length,
            props.edition.hasShortLink,
            props.edition.from
        ) > 0 || errorMessage
    );
    const isAudienceSelectionViewButtonDisabled = Boolean(errorMessage || !recipientCount);

    const pages = [
        {
            id: 'prepare',
            component: (
                <CheckRequiredItems
                    onClose={props.onClose}
                    isLoading={props.isLoading}
                    userFacingNoun={userFacingNoun}
                    characterCount={props.edition.content.length}
                    editionContent={props.edition.content}
                    hasShortLink={props.edition.hasShortLink}
                    optOutText={props.edition.optOutText}
                    canAccessSetup={Boolean(user && user.permissions.canAccessSetup)}
                    from={props.edition.from}
                />
            ),
        },
        {
            id: 'schedule',
            component: (
                <NewScheduleView
                    scheduledDate={scheduledDate}
                    setScheduledDate={setScheduledDate}
                    onReviewAndSend={() => {
                        // clear out scheduling states
                        props.setIsScheduling(false);
                        props.setIsRescheduling(false);
                        setCurrentView('review');
                    }}
                    type={props.type}
                    onLoad={() => {
                        setVisitedSchedulePage(true);
                    }}
                />
            ),
        },
        {
            id: 'review',
            component: (
                <ReviewAndSend
                    name={props.edition.name || 'Broadcast'}
                    from={props.from}
                    content={props.edition.content}
                    optOutText={props.edition.optOutText}
                    userFacingNoun={userFacingNoun}
                    selectedRecipientList={selectedRecipients}
                />
            ),
        },
    ];

    const shouldHideAudienceSelection =
        Boolean(props.edition.audience) && !NutClientConfig.hasNewAudienceManagementNUT14721;

    // RCFX instances choose an audience on the details page (old send flow), so they don't
    // need to see the audience selection page
    if (!shouldHideAudienceSelection) {
        pages.splice(1, 0, {
            id: 'recipients-selection',
            component: (
                <NewRecipientsSelectionView
                    selectedRecipients={selectedRecipients}
                    setSelectedRecipients={setSelectedRecipients}
                    canAccessFullCampaigns={canAccessFullCampaigns}
                    useSmsStats={NutClientConfig.hasSmsAudiencesFF}
                />
            ),
        });
    }

    const handleSend = () => {
        if (selectedRecipients) {
            setIsShowingSendConfirmation(false);
            if (
                selectedRecipients.type === RECIPIENT_TYPE_SAVED_LIST &&
                selectedRecipients.recipientSelection
            ) {
                sendEdition({
                    editionId: props.edition.id,
                    filterString: selectedRecipients.recipientSelection.filterString,
                    savedFilterId: selectedRecipients.recipientSelection.id,
                    entityType: selectedRecipients.recipientSelection.entityType,
                });
            } else if (selectedRecipients.recipientSelection) {
                sendEditionAudience({
                    variables: {
                        edition: {
                            editionId: props.edition.id,
                            audienceId: selectedRecipients.recipientSelection.id,
                        },
                    },
                });
            }
        }
    };

    const handleSchedule = () => {
        if (selectedRecipients && scheduledDate && selectedRecipients.recipientSelection) {
            if (selectedRecipients.type === RECIPIENT_TYPE_SAVED_LIST) {
                sendEdition({
                    editionId: props.edition.id,
                    filterString: selectedRecipients.recipientSelection.filterString,
                    savedFilterId: selectedRecipients.recipientSelection.id,
                    entityType: selectedRecipients.recipientSelection.entityType,
                    scheduledTime: scheduledDate.unix(),
                });
            } else {
                sendEditionAudience({
                    variables: {
                        edition: {
                            editionId: props.edition.id,
                            scheduledTime: scheduledDate.unix(),
                            audienceId: selectedRecipients.recipientSelection.id,
                        },
                    },
                });
            }
            setIsShowingSendConfirmation(false);
        }
    };

    function getListSizeWarning(): React.ReactNode | null | undefined {
        if (recipientCount && recipientCount > MAX_BROADCAST_RECIPIENTS) {
            return (
                <Body color='error'>
                    List cannot contain more than {MAX_BROADCAST_RECIPIENTS} people
                </Body>
            );
        }
        if (selectedRecipients && recipientCount === 0) {
            return <Body color='error'>This list has no members</Body>;
        }
    }

    return (
        <>
            <PrepareToSendWrapper
                errorMessage={errorMessage}
                shouldDisable={shouldDisable}
                showUpgradeBanner={showUpgradeBanner}
                isLoading={props.isLoading}
                // @ts-expect-error
                content={<TabbedContent selectedTab={currentView} tabOptions={pages} />}
                userFacingNoun={userFacingNoun}
                isFirstPage={currentView === 'prepare'}
                onClose={props.onClose}
                footerBar={
                    <FooterBar
                        left={
                            <PrepareToSendBreadcrumbs
                                prepareViewButtonsDisabled={isPrepareViewButtonDisabled}
                                audienceSelectionViewButtonsDisabled={
                                    isAudienceSelectionViewButtonDisabled
                                }
                                scheduleViewButtonsDisabled={!scheduledDate}
                                currentView={currentView}
                                setCurrentView={setCurrentView}
                                sendToText={sendToText}
                                isScheduling={props.isScheduling}
                                scheduledDate={
                                    visitedSchedulePage
                                        ? scheduledDate.format(
                                              FormattedTimestamp.formats.longMonthDayYearWithTime
                                          )
                                        : undefined
                                }
                                shouldHideAudienceSelection={shouldHideAudienceSelection}
                            />
                        }
                        right={
                            <PrepareToSendButtons
                                prepareViewButtonsDisabled={isPrepareViewButtonDisabled}
                                audienceSelectionViewButtonsDisabled={
                                    isAudienceSelectionViewButtonDisabled
                                }
                                scheduleViewButtonsDisabled={!scheduledDate}
                                saveButtonDisabled={
                                    isPrepareViewButtonDisabled ||
                                    isAudienceSelectionViewButtonDisabled
                                }
                                currentView={currentView}
                                setCurrentView={setCurrentView}
                                setIsShowingSendConfirmation={setIsShowingSendConfirmation}
                                setSendEmailButtonRef={setSendButtonRef}
                                recipientCount={recipientCount}
                                onCloseModal={props.routeToEngagement}
                                isSending={isSending || loading}
                                onSchedule={handleSchedule}
                                isScheduling={props.isScheduling}
                                setIsScheduling={props.setIsScheduling}
                                scheduledDate={scheduledDate}
                                getListSizeWarning={getListSizeWarning}
                                isAllowedToSchedule={true}
                                shouldHideAudienceSelection={shouldHideAudienceSelection}
                            />
                        }
                    />
                }
            />
            {isShowingSendConfirmation && (
                <Dialog
                    anchor={sendButtonRef}
                    question={
                        typeof recipientCount === 'number'
                            ? `This message will be sent to ${recipientCount}
                                           ${recipientCount !== 1 ? 'recipients' : 'recipient'}.
                                            Are you sure you'd like to send this now?`
                            : // This case shouldn't ever happen - but default fallback message
                              'This cannot be undone, are you sure you’d like to send this now?'
                    }
                    primaryText='Yes, send now'
                    onPrimary={handleSend}
                    secondaryText='No, don’t send'
                    onSecondary={() => {
                        setIsShowingSendConfirmation(false);
                    }}
                />
            )}
        </>
    );
}
